import { useEffect, useState } from 'react';

import { Button, Dialog, DialogContent, Stack, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { v4 } from 'uuid';

import { BasicButton } from 'components/atoms/BasicButton';
import { Typography } from 'components/atoms/Typography';
import useAxios from 'hooks/api/useAxios';
import { MapTemplate } from 'types/api/mapTemplate';

import { MapConfig } from '../../MapConfigCreator';

type MapTemplateCreatorProps = {
    onSuccess?: (template: MapTemplate) => void;
    defaultConfig?: MapConfig;
};

export const MapTemplateCreator = ({
    onSuccess = () => undefined,
    defaultConfig,
}: MapTemplateCreatorProps): JSX.Element => {
    const [open, setOpen] = useState(false);

    const [name, setName] = useState('');

    const onClose = (): void => {
        setOpen(false);
    };

    const {
        register,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<{ name: string }>();

    const [res, { rerun }] = useAxios<MapTemplate>(
        {
            url: '/api/builder/map/template/add/',
            method: 'POST',
            skipRequest: () => true,
            data: {
                name,
                uuid: v4(),
                config: defaultConfig || {},
            },
        },
        [name]
    );

    const loading = res.type === 'loading';
    const error = res.type === 'error';
    const success = res.type === 'success' && res.data;

    const onSubmit = handleSubmit((data) => {
        setName(data.name);
        rerun();
    });

    useEffect(() => {
        setName('');
        reset();
    }, [open, reset]);

    useEffect(() => {
        if (success) {
            onSuccess(success);
            onClose();
        }
    }, [onSuccess, success]);

    return (
        <>
            <Button
                variant="outlined"
                sx={{ ml: 'auto', mr: 1 }}
                onClick={() => setOpen(true)}
            >
                {defaultConfig
                    ? 'Сохранить как новый шаблон'
                    : 'Добавить новый'}
            </Button>
            <Dialog open={open}>
                <DialogContent sx={{ maxWidth: '420px', padding: '32px 40px' }}>
                    <Stack
                        spacing={3}
                        alignItems="center"
                        component="form"
                        onSubmit={onSubmit}
                    >
                        {!error ? (
                            <Typography
                                variant="title.large"
                                textAlign="center"
                            >
                                Введите название шаблона
                            </Typography>
                        ) : (
                            <Typography
                                variant="title.large"
                                textAlign="center"
                            >
                                Что-то пошло не так.
                                <br />
                                Не удалось добавить шаблон.
                            </Typography>
                        )}
                        <TextField
                            label="Название"
                            variant="outlined"
                            fullWidth
                            error={!!errors.name}
                            disabled={loading}
                            {...register('name', {
                                required: true,
                                pattern: /^[^\s]+(?:$|.*[^\s]+$)/,
                            })}
                        />
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ width: '100%' }}
                        >
                            <BasicButton
                                variant="secondary"
                                onClick={() => onClose()}
                                disabled={loading}
                            >
                                ОТМЕНА
                            </BasicButton>
                            <BasicButton
                                type="submit"
                                $bgColor="green"
                                disabled={loading || !!errors.name}
                            >
                                ДОБАВИТЬ
                            </BasicButton>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};
